import { useQuery } from '@tanstack/react-query'
import { Plug } from 'lucide-react'
import { systemApi, llmReasoningApi } from '../api/client'
import { useExpandablePanel } from '../hooks/useExpandablePanel'
import { ExpandToggle } from './ExpandToggle'
import './ServicesStatus.css'

const dotClass = (connected: boolean, circuit: string) =>
  circuit === 'open' ? 'svc-dot danger' : connected ? 'svc-dot ok' : 'svc-dot idle'

const periodLabel = (s: number) => (s >= 3600 ? 'hr' : s >= 60 ? 'min' : `${s}s`)

export function ServicesStatus() {
  const { expanded, toggle, scrollClassName } = useExpandablePanel('services')
  const { data, isLoading, isError } = useQuery({
    queryKey: ['services-status'],
    queryFn: systemApi.getServices,
    refetchInterval: 15000,
  })

  const { data: llm } = useQuery({
    queryKey: ['llm-reasoning-usage'],
    queryFn: llmReasoningApi.usage,
    refetchInterval: 30000,
  })

  const services = data?.services ?? []
  const upCount = services.filter((s) => s.connected && s.circuit_state !== 'open').length

  const detailFor = (name: string, fallback: string | null): string => {
    if (name === 'anthropic' && llm) {
      const pct = llm.daily_budget_usd > 0 ? Math.round((llm.cost_today_usd / llm.daily_budget_usd) * 100) : 0
      return `$${llm.cost_today_usd.toFixed(2)} / $${llm.daily_budget_usd} today · ${pct}%`
    }
    return fallback ?? '—'
  }

  return (
    <div className="card services-status-card">
      <div className="card-header">
        <h3 className="card-title">
          <Plug size={20} className="icon" />
          Services / API Status
        </h3>
        <div className="card-header-right">
          {services.length > 0 && (
            <span className="card-badge">{upCount}/{services.length} up</span>
          )}
          <ExpandToggle expanded={expanded} onToggle={toggle} />
        </div>
      </div>

      {isLoading ? (
        <div className="loading"><div className="spinner" /></div>
      ) : isError ? (
        <div className="empty-state"><div className="empty-text">Failed to load service status</div></div>
      ) : (
        <ul className={`services-list ${scrollClassName}`}>
          {services.map((s) => (
            <li className="svc-row" key={s.name}>
              <span className={dotClass(s.connected, s.circuit_state)} aria-hidden="true" />
              <span className="svc-name">{s.name}</span>
              <span className="svc-detail">{detailFor(s.name, s.detail)}</span>
              {s.rate_limit ? (
                <span className="svc-rl mono">
                  {s.rate_limit.remaining}/{s.rate_limit.limit} {periodLabel(s.rate_limit.period_seconds)}
                </span>
              ) : (
                <span className="svc-rl mono muted">—</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
